import {useInfiniteQuery} from '@tanstack/react-query'
import {startOfDay} from 'date-fns'
import sub from 'date-fns/sub'
import moment from 'moment'
import {useEffect, useMemo, useState} from 'react'
import api, {Alarm, EventRelationsType, FilterEventsType, SearchEventsType} from 'src/services/api'
import {QueryKeyEnum} from 'src/utilities/enums/QueryKeyEnum'
import {FaceDetectionWSChannelEnum, WebSocketRoomTypeEnum} from 'src/utilities/enums/WSChannelEnums'
import {ListObjectIdEnum} from '../../../pages/alarms/AlarmsFunctions'
import useWebsocket from '../socket/useWebsocket'

const BATCH_SIZE = 20

export interface EventsWebsocketParams {
  filter?: {
    'status.description'?: string[]
    alarmTypeId?: number[]
    companyId?: number
  }
  search?: {
    id?: number
    'camera.name'?: string
    'client.name'?: string
  }
}


type Props = {
  filters?: FilterEventsType
  search?: SearchEventsType
  labelSelectedOnCalendar?: string
}

export default function useEvents({filters, search, labelSelectedOnCalendar}: Props) {
  const [events, setEvents] = useState<Alarm[]>([])
  const filtersKey = JSON.stringify(filters)
  const searchKey = JSON.stringify(search)

  useEffect(() => {
    setEvents([])
  }, [filtersKey, searchKey])

  const onMessage = (event: Alarm) => {
    setEvents((prev) => {
      const newEvents = [...prev]
      if (filters?.statuses?.length) {
        if (filters?.statuses.includes(event.status.description)) newEvents.push(event)
      }
      if (filters?.eventTypeIds?.length) {
        if (filters?.eventTypeIds.includes(String(event.eventTypeId))) newEvents.push(event)
      }
      if (!filters?.statuses?.length) newEvents.push(event)

      const newDetections = newEvents
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .filter((detection, index, self) => index === self.findIndex((t) => t.id === detection.id))
        .filter((detection) => filters?.companyIds?.includes(detection.companyId))

      return newDetections
    })
  }

  const websocket = useWebsocket({
    room: WebSocketRoomTypeEnum.EVENTS,
    listenTo: [FaceDetectionWSChannelEnum.EVENTS],
    onMessage,
    enable: true,
  })

  const wsFilter = useMemo<EventsWebsocketParams>(
    () => ({
      filter: {
        companyId: filters?.companyIds?.length === 1 ? filters.companyIds[0] : undefined,
        'status.description': filters?.statuses?.length ? filters.statuses : undefined,
        alarmTypeId: filters?.eventTypeIds?.length ? filters.eventTypeIds.map(Number) : undefined,
      },
      search: {
        id: search?.id,
        'camera.name': search?.cameraName,
        'client.name': search?.clientName,
      },
    }),
    [filters?.statuses, filters?.eventTypeIds, search?.id, search?.cameraName, search?.clientName],
  )

  const filterJson = JSON.stringify(wsFilter.filter)
  const searchJson = JSON.stringify(wsFilter.search)

  useEffect(() => {
    const socket = websocket.socket
    if (!socket) return

    const payload = {room: WebSocketRoomTypeEnum.EVENTS, filter: wsFilter.filter, search: wsFilter.search}
    const sendFilter = () => socket.emit('filter', payload)

    if (socket.connected) sendFilter()
    socket.on('connect', sendFilter)

    return () => {
      socket.off('connect', sendFilter)
      socket.emit('filter', {room: WebSocketRoomTypeEnum.EVENTS})
    }
  }, [websocket.socket, filterJson, searchJson])

  const handleUpdateTimeFilterWhenFiltersChange = () => {
    const valuesOnRuler = [
      {updateTime: [moment(sub(new Date(), {minutes: 15})).format(), moment(new Date()).format()], label: '15min'},
      {updateTime: [moment(sub(new Date(), {minutes: 30})).format(), moment(new Date()).format()], label: '30min'},
      {updateTime: [moment(sub(new Date(), {hours: 1})).format(), moment(new Date()).format()], label: '1h'},
      {updateTime: [moment(sub(new Date(), {hours: 6})).format(), moment(new Date()).format()], label: '6h'},
      {updateTime: [moment(sub(new Date(), {hours: 12})).format(), moment(new Date()).format()], label: '12h'},
      {updateTime: [moment(startOfDay(new Date())).format(), moment(new Date()).format()], label: 'Hoje'},
    ]
    return valuesOnRuler.find((it) => it.label === labelSelectedOnCalendar)?.updateTime
  }

  const query = useInfiniteQuery({
    retry: 0,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    queryKey: [QueryKeyEnum.EVENTS, filters, search],
    queryFn: async ({pageParam: nextId}) => {
      if (nextId === -1) throw new Error('No more pages to fetch')

      const includes: Array<EventRelationsType> = ['camera', 'status', 'snapshots', 'snapshots.demarcatedAreas', 'activatedActions']
      filters?.eventTypeIds?.forEach((value) => {
        if (ListObjectIdEnum.LINE_CROSSING === value) includes.push('lineCrossingLogs')
      })

      const response = await api.events.getMany({
        batchSize: BATCH_SIZE,
        startId: nextId === 0 ? null : nextId,
        search,
        filter: filters,
        orderBy: {ids: 'DESC'},
        includes: includes,
      })

      return response
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage && allPages && allPages.some((c) => c && c.data.data.items.some((detection) => detection.id === lastPage.data.data.nextId))) {
        return null
      }

      return lastPage ? lastPage.data.data.nextId : undefined
    },
    throwOnError: () => {
      return false
    },
  })

  const getEventData = (events: Alarm[]) => {
    return events
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .filter((event, index, self) => index === self.findIndex((t) => t.id === event.id))
  }

  const disconnectWebsocket = () => {
    websocket.forceDisconnect()
    setEvents([])
  }

  return {
    ...query,
    webSocketConnectionStatus: websocket?.webSocketConnectionStatus,
    forceConnect: websocket.forceConnect,
    forceDisconnect: disconnectWebsocket,
    data: getEventData([...events, ...(query.data?.pages.flatMap((page) => page.data.data.items) ?? [])]),
    events,
    handleUpdateTimeFilterWhenFiltersChange,
  }
}
